import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAnalyses, downloadExcel, deleteAnalysis } from '../../services/analyses';
import AnalysisProgress from './AnalysisProgress';
import Pagination from '../common/Pagination';
import './HistoryTable.css';

const HistoryTable = ({ onPreview }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [total, setTotal] = useState(0);
  const itemsPerPage = 10;

  // Página, búsqueda y filtro en la URL: al abrir un plano esta tabla se desmonta,
  // así que si el estado vive solo aquí se pierde y se vuelve a la primera página.
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
  const search = searchParams.get('search') || '';
  const onlyWithComparison = searchParams.get('saved') === '1';

  const updateParams = (changes, replace = false) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '' || value === false || value === 1) next.delete(key);
      else next.set(key, value === true ? '1' : String(value));
    });
    setSearchParams(next, { replace });
  };

  const setCurrentPage = (target) => updateParams({ page: target });

  useEffect(() => {
    const debounce = setTimeout(() => loadAnalyses(currentPage), search ? 300 : 0);
    const interval = setInterval(() => loadAnalyses(currentPage), 10000);

    return () => {
      clearTimeout(debounce);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, onlyWithComparison, search]);

  const loadAnalyses = async (page = 1) => {
    try {
      const skip = (page - 1) * itemsPerPage;
      const data = await getAnalyses(skip, itemsPerPage, onlyWithComparison, search);
      setAnalyses(data.analyses || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error loading analyses:', error);
    } finally {
      setLoading(false);
    }
  };

  // LAKPED-13: al cambiar el filtro se vuelve a la primera página
  const handleToggleComparisonFilter = (checked) => updateParams({ saved: checked, page: 1 });

  // replace al escribir: si no, cada letra deja una entrada en el historial del navegador
  const handleSearch = (value) => updateParams({ search: value, page: 1 }, true);

  const openSavedComparison = (analysisHash) => {
    window.open(`/similarity-search?hash=${analysisHash}&compare=1`, '_blank');
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedAnalyses = [...analyses].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'created') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      failed: 'Fallido',
      skipped: 'No es plano',
      invoice: 'Factura/Albarán',
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    const classMap = {
      pending: 'status-pending',
      processing: 'status-processing',
      completed: 'status-completed',
      failed: 'status-failed',
      skipped: 'status-skipped',
      invoice: 'status-invoice',
    };
    return classMap[status] || '';
  };

  const handleDownload = async (analysisHash, filename, created) => {
    try {
      const fecha = new Date(created).toISOString().slice(0,10).replace(/-/g, '');
      const baseName = filename.replace(/\.[^/.]+$/, '');
      const excelFilename = `analisis_${baseName}_${fecha}.xlsx`;
      await downloadExcel(analysisHash, excelFilename);
    } catch (error) {
      console.error('Error downloading excel:', error);
      alert('Error al descargar el Excel');
    }
  };

  const handleDeleteFromHistory = async (analysisHash) => {
    if (window.confirm('¿Está seguro de eliminar este análisis?')) {
      try {
        await deleteAnalysis(analysisHash);
        await loadAnalyses(currentPage);
      } catch (error) {
        console.error('Error deleting analysis:', error);
        alert('Error al eliminar el análisis');
      }
    }
  };

  if (loading) {
    return <div className="loading">Cargando historial...</div>;
  }

  return (
    <div className="history-container">
      <h1 className="history-title">Historial de análisis realizados</h1>

      <div className="history-toolbar">
        <div className="history-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Buscar por nombre de plano"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {search && (
            <button className="history-search-clear" onClick={() => handleSearch('')} title="Limpiar">×</button>
          )}
        </div>

        <label className="comparison-filter">
          <input
            type="checkbox"
            checked={onlyWithComparison}
            onChange={(e) => handleToggleComparisonFilter(e.target.checked)}
          />
          <span>Solo con comparativa guardada</span>
        </label>
      </div>

      <table className="history-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('created')}>
              Fecha {sortField === 'created' && (sortOrder === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('filename')}>
              Usuario {sortField === 'filename' && (sortOrder === 'asc' ? '↑' : '↓')}
            </th>
            <th>Plano</th>
            <th>Análisis excel</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sortedAnalyses.map((analysis) => (
            <tr key={analysis.hash}>
              <td>{formatDate(analysis.created)}</td>
              <td>{analysis.created_by?.first_name || 'Usuario'}</td>
              <td>
                <div className="plano-nombre">{analysis.filename}</div>
                {analysis.has_comparison && (
                  <button
                    className="plano-comparativa"
                    onClick={() => openSavedComparison(analysis.hash)}
                    title="Abrir la comparativa de similares guardada de este plano"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 7h7M4 17h7M15 4l5 5-5 5" />
                    </svg>
                    Comparativa guardada
                  </button>
                )}
              </td>
              <td>
                {analysis.status === 'completed' ? (
                  <div className="analysis-cell">
                    <span className="analysis-name">
                      análisis-{formatDate(analysis.created)}-{analysis.hash.substring(0, 6)}
                    </span>
                    <div className="analysis-links">
                      {analysis.result && (
                        <button className="btn-link" onClick={() => onPreview(analysis)}>
                          previsualizar
                        </button>
                      )}
                    </div>
                  </div>
                ) : analysis.status === 'processing' || analysis.status === 'pending' ? (
                  <AnalysisProgress
                    analysisHash={analysis.hash}
                    onComplete={() => loadAnalyses(currentPage)}
                  />
                ) : (
                  <span
                    className={`status-badge ${getStatusClass(analysis.status)}`}
                    title={analysis.status === 'failed' && analysis.progress_message ? analysis.progress_message : ''}
                  >
                    {analysis.status === 'failed' && analysis.progress_message
                      ? 'Error - hover para ver detalles'
                      : getStatusDisplay(analysis.status)}
                  </span>
                )}
              </td>
              <td>
                <div className="action-buttons">
                  {analysis.status === 'completed' && (
                    <button
                      className="btn-download"
                      onClick={() => handleDownload(analysis.hash, analysis.filename, analysis.created)}
                    >
                      ⬇
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => handleDeleteFromHistory(analysis.hash)}
                    title="Eliminar"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination
        page={currentPage}
        totalPages={totalPages || 1}
        totalItems={total}
        itemLabel={total === 1 ? 'análisis' : 'análisis'}
        loading={loading}
        onChange={setCurrentPage}
      />

    </div>
  );
};

export default HistoryTable;